/**
 * Markups Section Component
 * BuilderBel Construction Project Management System
 *
 * Collapsible section for managing estimate markups.
 * Fields match backend EstimateMarkup model and Joi validators exactly.
 */

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Plus,
  Edit2,
  Trash2,
  ChevronDown,
  ChevronUp,
  Percent,
  DollarSign,
  RefreshCw,
} from 'lucide-react';
import type {
  EstimateMarkup,
  CreateMarkupRequest,
  UpdateMarkupRequest,
  MarkupType,
  MarkupCalculationMethod,
  MarkupApplyTo,
} from '../../types/estimate';

export interface MarkupsSectionProps {
  markups: EstimateMarkup[];
  subtotalDirect: number;
  subtotalLabor: number;
  subtotalMaterial: number;
  subtotalEquipment: number;
  subtotalSubcontractor: number;
  onAdd: (data: CreateMarkupRequest) => void;
  onUpdate: (markupId: string, data: UpdateMarkupRequest) => void;
  onDelete: (markupId: string) => void;
  onRecalculate: () => void;
  isEditable?: boolean;
  isLoading?: boolean;
}

interface MarkupFormData {
  name: string;
  type: MarkupType;
  calculationMethod: MarkupCalculationMethod;
  percentage: number;
  fixedAmount: number;
  applyTo: MarkupApplyTo;
  description: string;
}

/** Backend: MARKUP_TYPE_VALUES — 'fee' is NOT valid */
const markupTypeOptions: { value: MarkupType; label: string }[] = [
  { value: 'overhead', label: 'Overhead' },
  { value: 'profit', label: 'Profit' },
  { value: 'contingency', label: 'Contingency' },
  { value: 'bond', label: 'Bond' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'tax', label: 'Tax' },
  { value: 'other', label: 'Other' },
];

/** Backend: MARKUP_APPLY_TO_VALUES — 'equipment'/'subcontractor' are NOT valid */
const applyToOptions: { value: MarkupApplyTo; label: string }[] = [
  { value: 'all', label: 'All Costs' },
  { value: 'labor', label: 'Labor Only' },
  { value: 'material', label: 'Material Only' },
  { value: 'subtotal', label: 'Subtotal' },
  { value: 'custom', label: 'Custom Cost Codes' },
];

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value || 0);
};

const formatPercent = (value: number): string => {
  return `${(value || 0).toFixed(2)}%`;
};

const MarkupRow: React.FC<{
  markup: EstimateMarkup;
  onEdit: () => void;
  onDelete: () => void;
  isEditable?: boolean;
}> = ({ markup, onEdit, onDelete, isEditable = true }) => {
  const isPercentage = markup.calculationMethod === 'percentage';

  return (
    <div className="flex items-center justify-between py-3 px-4 hover:bg-gray-50 group">
      <div className="flex items-center gap-3">
        <div className={`p-1.5 rounded-lg ${isPercentage ? 'bg-purple-50' : 'bg-blue-50'}`}>
          {isPercentage ? (
            <Percent className="h-4 w-4 text-purple-600" />
          ) : (
            <DollarSign className="h-4 w-4 text-blue-600" />
          )}
        </div>
        <div>
          <span className="text-sm font-medium text-gray-900">{markup.name}</span>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-gray-500 capitalize">{markup.type}</span>
            <span className="text-xs text-gray-300">|</span>
            <span className="text-xs text-gray-500">
              Apply to: {markup.applyTo === 'all' ? 'All' : markup.applyTo}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="text-right">
          <span className="text-sm font-medium text-gray-900">
            {isPercentage
              ? formatPercent(markup.percentage || 0)
              : formatCurrency(markup.fixedAmount || 0)}
          </span>
          <span className="block text-xs text-gray-500">= {formatCurrency(markup.amount)}</span>
        </div>

        {isEditable && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={onEdit}
              className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600"
            >
              <Edit2 className="h-4 w-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const MarkupsSection: React.FC<MarkupsSectionProps> = ({
  markups,
  subtotalDirect,
  subtotalLabor,
  subtotalMaterial,
  subtotalEquipment,
  subtotalSubcontractor,
  onAdd,
  onUpdate,
  onDelete,
  onRecalculate,
  isEditable = true,
  isLoading = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMarkup, setEditingMarkup] = useState<EstimateMarkup | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<MarkupFormData>({
    defaultValues: {
      name: '',
      type: 'overhead',
      calculationMethod: 'percentage',
      percentage: 0,
      fixedAmount: 0,
      applyTo: 'all',
      description: '',
    },
  });

  const watchCalculationMethod = watch('calculationMethod');

  const totalMarkups = markups.reduce((sum, m) => sum + (m.amount || 0), 0);
  const grandTotal = (subtotalDirect || 0) + totalMarkups;

  const handleAddClick = () => {
    setEditingMarkup(null);
    reset({
      name: '',
      type: 'overhead',
      calculationMethod: 'percentage',
      percentage: 0,
      fixedAmount: 0,
      applyTo: 'all',
      description: '',
    });
    setShowAddForm(true);
  };

  const handleEditClick = (markup: EstimateMarkup) => {
    setEditingMarkup(markup);
    reset({
      name: markup.name,
      type: markup.type,
      calculationMethod: markup.calculationMethod,
      percentage: markup.percentage || 0,
      fixedAmount: markup.fixedAmount || 0,
      applyTo: markup.applyTo,
      description: markup.description || '',
    });
    setShowAddForm(true);
  };

  const handleFormSubmit = (data: MarkupFormData) => {
    const isPercentage = data.calculationMethod === 'percentage';
    const payload = {
      name: data.name,
      type: data.type,
      calculationMethod: data.calculationMethod,
      percentage: isPercentage ? data.percentage : undefined,
      fixedAmount: !isPercentage ? data.fixedAmount : undefined,
      applyTo: data.applyTo,
      description: data.description || undefined,
    };

    if (editingMarkup) {
      onUpdate(editingMarkup._id, payload as UpdateMarkupRequest);
    } else {
      onAdd(payload as CreateMarkupRequest);
    }
    setShowAddForm(false);
    setEditingMarkup(null);
    reset();
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingMarkup(null);
    reset();
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-6 py-4 bg-gray-50 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <Percent className="h-5 w-5 text-purple-600" />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Markups</h3>
            <span className="text-xs text-gray-500">
              {markups.length} items | {formatCurrency(totalMarkups)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isEditable && (
            <>
              <button
                onClick={e => {
                  e.stopPropagation();
                  onRecalculate();
                }}
                disabled={isLoading}
                className="p-2 rounded-lg hover:bg-gray-200 text-gray-500 hover:text-gray-700"
                title="Recalculate markups"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleAddClick();
                }}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700"
              >
                <Plus className="h-3 w-3" />
                Add
              </button>
            </>
          )}
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div>
          {/* Add/Edit Form */}
          {showAddForm && (
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              className="p-4 bg-gray-50 border-b border-gray-200"
            >
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Name *</label>
                  <input
                    type="text"
                    {...register('name', { required: 'Name is required' })}
                    placeholder="e.g., General Overhead"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1">{errors.name.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                  <select
                    {...register('type')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    {markupTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Apply To</label>
                  <select
                    {...register('applyTo')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    {applyToOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Calculation Method
                  </label>
                  <select
                    {...register('calculationMethod')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {watchCalculationMethod === 'percentage'
                      ? 'Percentage (%)'
                      : 'Fixed Amount ($)'}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {watchCalculationMethod === 'percentage' ? '%' : '$'}
                    </span>
                    {watchCalculationMethod === 'percentage' ? (
                      <input
                        type="number"
                        step="0.01"
                        {...register('percentage', {
                          required: 'Percentage is required',
                          valueAsNumber: true,
                        })}
                        className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    ) : (
                      <input
                        type="number"
                        step="0.01"
                        {...register('fixedAmount', {
                          required: 'Amount is required',
                          valueAsNumber: true,
                        })}
                        className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                    )}
                  </div>
                </div>
                <div className="col-span-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    {...register('description')}
                    placeholder="Optional description"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-1.5 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50"
                >
                  {editingMarkup ? 'Update' : 'Add Markup'}
                </button>
              </div>
            </form>
          )}

          {/* Markup List */}
          <div className="divide-y divide-gray-100">
            {markups.length === 0 ? (
              <div className="py-8 text-center text-gray-500 text-sm">
                No markups added yet.
                {isEditable && (
                  <button
                    onClick={handleAddClick}
                    className="block mx-auto mt-2 text-brand-600 hover:text-brand-700"
                  >
                    Add your first markup
                  </button>
                )}
              </div>
            ) : (
              markups.map(markup => (
                <MarkupRow
                  key={markup._id}
                  markup={markup}
                  onEdit={() => handleEditClick(markup)}
                  onDelete={() => onDelete(markup._id)}
                  isEditable={isEditable}
                />
              ))
            )}
          </div>

          {/* Summary */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-sm text-gray-600">Subtotal</span>
                <span className="ml-2 text-sm font-medium text-gray-900">
                  {formatCurrency(subtotalDirect || 0)}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-600">+ Markups</span>
                <span className="ml-2 text-sm font-medium text-purple-600">
                  {formatCurrency(totalMarkups)}
                </span>
              </div>
              <div>
                <span className="text-sm font-semibold text-gray-900">Grand Total</span>
                <span className="ml-2 text-lg font-bold text-green-600">
                  {formatCurrency(grandTotal)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkupsSection;
